import { FRANCHISE_PRODUCT_NAME } from "@nh/shared";
import type { FranchiseIconKey } from "./franchise.icons";

/**
 * El CONTENIDO de la landing de franquicias, como datos.
 *
 * ══════════════════════════════════════════════════════════════════════
 * ESTO ES CONFIGURACIÓN, Y HOY ESTÁ ESCRITA A MANO
 *
 * En el sistema actual todo lo de aquí abajo lo edita el super-admin: se
 * guarda en el documento `settings/franchise` y la landing lo lee al
 * montar. Este archivo es esa misma configuración, con la misma forma,
 * congelada en el código porque New Horizons todavía no tiene el endpoint
 * que la sirva.
 *
 * Cuando exista, el cambio es de una línea en `useFranchiseSections`: en
 * vez de importar `FRANCHISE_CONTENT`, llamar al hook que trae la config.
 * Por eso la forma se respeta al detalle aunque hoy sobre — incluido
 * `productName`, que no se pinta en ninguna parte y existe solo para el
 * cobro (es el nombre que sale en el recibo de Stripe).
 *
 * ── Qué NO está aquí, y por qué ──────────────────────────────────────
 * El `FranchiseConfig` de producción trae además `hero`, `niveles`,
 * `ctaNote` y `priceSuffix`. Son campos de la OTRA landing, la que vive
 * dentro del panel (`FranchiseLandingPage`) — y esa página no se va a
 * usar en este sistema, así que su contenido tampoco entra: dejarlo sería
 * un molde vacío que nadie lee y que el día de mañana alguien traduce por
 * error. Si esa pantalla resucitara, vuelven con ella.
 * ══════════════════════════════════════════════════════════════════════
 *
 * ── Las cadenas son claves de i18n ───────────────────────────────────
 * El español es la fuente y también la clave; cada componente las pasa
 * por `t()`. Las traducciones viven en `shared/i18n/locales/franchise.ts`.
 * El día que el contenido venga de un endpoint, lo que llegue por ahí ya
 * será texto final y `t()` lo devolverá tal cual: una clave sin
 * traducción se devuelve a sí misma, así que la transición no rompe nada.
 */

export interface FranchiseBenefit {
  /**
   * El icono que le toca en el panel de configuración.
   *
   * La landing pública NO lo pinta —sus tarjetas son título y frase, sin
   * icono—, pero el campo forma parte de la configuración y viaja con
   * ella. Ver `franchise.icons.ts`.
   */
  icon: FranchiseIconKey;
  title: string;
  desc: string;
}

/** Una ventaja económica de tener la franquicia: la cifra y qué es. */
export interface FranchiseAdvantage {
  /** La cifra grande ("10 %", "20 %"). Texto: no se calcula. */
  pct: string;
  label: string;
}

export interface FranchiseConfig {
  content: {
    beneficiosTitle: string;
    beneficios: readonly FranchiseBenefit[];
    costLabel: string;
    /** Lo que se lee junto a la moneda, tras el importe. */
    costUnit: string;
    costTagline: string;
    ventajasTitle: string;
    ventajas: readonly FranchiseAdvantage[];
    ctaLabel: string;
    /** Nombre del cargo en la pasarela. No se pinta: es para el recibo. */
    productName: string;
  };
}

export const FRANCHISE_CONTENT: FranchiseConfig = {
  /*
   * El PRECIO no vive aquí (14-sep-2026): lo pone Anahí en Admin →
   * Franquicias y la página lo lee con `useFranchiseSettings`, la misma
   * fila que el servidor cobra.
   */
  content: {
    beneficiosTitle: "Lo que recibes",
    beneficios: [
      {
        icon: "badge-check",
        title: "Tu nombre en la plataforma",
        desc: "Tu tienda vive dentro de It's The Best, con tu propia dirección.",
      },
      {
        icon: "palette",
        title: "Branding propio de tu equipo",
        desc: "Tu logo y tu color en toda la tienda que administras.",
      },
      {
        icon: "trending-up",
        title: "Promociones sobre el authorship",
        desc: "Ajustas precios hacia arriba; nunca por debajo del authorship.",
      },
      {
        icon: "power",
        title: "Habilitas o deshabilitas productos",
        desc: "Tú decides qué productos aparecen en tu tienda.",
      },
    ],

    costLabel: "Costo de tu franquicia",
    costUnit: "al año · por usuario",
    costTagline: "No es una membresía: es tu propiedad.",

    ventajasTitle: "Lo que ganas con tu franquicia",
    /*
     * ⚠️ ESTAS CIFRAS SON UNA PROMESA PÚBLICA
     *
     * Hasta el 14-sep-2026 aquí se anunciaba el bono directo de
     * franquicia (30·10·9·5·2 = 55 %) que Anahí QUITÓ ese día. Lo que
     * hay ahora es lo que el sistema de verdad hace: el 10 % de
     * franquiciatario (`FRANCHISEE_DISCOUNT_PERCENT`) y el bono de inicio
     * rápido del plan (`bir.levels`: 20·4·6·4·2), que cobra el socio
     * ACTIVO. Si el plan cambia, estas cifras se cambian a mano.
     */
    // Anahí (14-sep, capturas): los porcentajes del plan (20 % y 4·6·4·2 %)
    // NO se anuncian aquí. Quedan el descuento y la activación.
    ventajas: [
      { pct: "10 %", label: "menos en todos los productos y paquetes, sobre el precio de socio" },
      { pct: "50 PV", label: "al mes con tus compras te mantienen activo y cobrando" },
    ],

    ctaLabel: "Adquirir mi franquicia",
    /**
     * Tampoco se escribe aquí, y por la misma razón que el precio: es lo
     * que el SERVIDOR le pone al cargo en Stripe. Lo que aparece en el
     * estado de cuenta de quien compra lo decide el backend, no la
     * página.
     */
    productName: FRANCHISE_PRODUCT_NAME,
  },
};
